import { Boundaries } from "@planzone/core";
import { Event, events } from "@planzone/events";
import { setRequestStateToPending } from "../../utils/setRequestStateToPending";
import { setRequestStateToSuccess } from "../../utils/setRequestStateToSuccess";
import { setRequestStateToFailure } from "../../utils/setRequestStateToFailure";
import { createStateReducer } from "../../utils/createStateReducer";
import { resetState } from "../../utils/resetState";
import { reduceProject } from "../../common/project";
import { State, Selector } from "./types";
import { createSelector } from "./selector";
import { match } from "./utils";

const initialState: State = [];

function handleRequest(
  state: State,
  { resource, query }: Boundaries["retrieveResourceProjects"]["request"],
): State {
  const index = state.findIndex(
    (req) => req.resource.uuid === resource.uuid && match(req.query, query),
  );

  return index !== -1
    ? [
        ...state.slice(0, index),
        { ...state[index], request: setRequestStateToPending() },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          resource,
          request: setRequestStateToPending(),
          query,
          projects: [],
          cursor: query.start,
          canRetrieveNextProjects: true,
        },
      ];
}

function handleSuccess(
  state: State,
  {
    resource,
    query,
    projects,
  }: Boundaries["retrieveResourceProjects"]["success"],
): State {
  const index = state.findIndex(
    (req) => req.resource.uuid === resource.uuid && match(req.query, query),
  );

  return index !== -1
    ? [
        ...state.slice(0, index),
        {
          ...state[index],
          request: setRequestStateToSuccess(),
          projects:
            query.start === 0
              ? projects
              : [...state[index].projects, ...projects],
          cursor: query.start + 1,
          canRetrieveNextProjects: projects.length >= query.limit,
        },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          request: setRequestStateToSuccess(),
          resource,
          query,
          projects,
          cursor: query.start + 1,
          canRetrieveNextProjects: projects.length >= query.limit,
        },
      ];
}

function handleFailure(
  state: State,
  { resource, query }: Boundaries["retrieveResourceProjects"]["failure"],
): State {
  const index = state.findIndex(
    (req) => req.resource.uuid === resource.uuid && match(req.query, query),
  );

  return index !== -1
    ? [
        ...state.slice(0, index),
        { ...state[index], request: setRequestStateToFailure() },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          request: setRequestStateToSuccess(),
          resource,
          query,
          projects: [],
          cursor: query.start,
          canRetrieveNextProjects: false,
        },
      ];
}

function propagateEvent(state = initialState, event: Event): State {
  return state.map((req) => ({
    ...req,
    projects: req.projects.map((project) => reduceProject(project, event)),
  }));
}

function handleAddResourceToProjects(
  state: State,
  { resource }: Boundaries["addPlanzoneResourceToProjects"]["success"],
): State {
  return state.filter((req) => req.resource.uuid !== resource.uuid);
}

function handleAllocateResource(
  state: State,
  { allocations }: Boundaries["allocateResources"]["success"],
): State {
  return state.filter(
    (req) =>
      !allocations.find(({ resource }) => req.resource.uuid !== resource.uuid),
  );
}

const reduceState = resetState(
  initialState,
  createStateReducer(
    initialState,
    {
      [events.retrieveResourceProjectsRequested]: handleRequest,
      [events.retrieveResourceProjectsSucceeded]: handleSuccess,
      [events.retrieveResourceProjectsFailed]: handleFailure,
      [events.addPlanzoneResourceToProjectsSucceeded]:
        handleAddResourceToProjects,
      [events.allocateResourcesSucceeded]: () => initialState,
    },
    propagateEvent,
  ),
  [events.changePlanzoneSucceeded],
);

export type { State, Selector };

export { reduceState, initialState, createSelector };
